import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { baseURL } from "../../../utils";
import { apiClient } from "../../api";
import {
  EXPRESS_RATES_API_RESPONSE,
  ExpressRateReturnType,
  RATE_API_RESPONSE,
  RateReturnType,
  RATES_API_RESPONSE,
  RatesReturnType,
} from "./types";
import keys from "./keys";

export const ratesResourcePath = "business-rates";

interface UseGetExpressRatesOptions {
  refetchInterval?: number;
  staleTime?: number;
  enabled?: boolean;
}

const useGetExpressRates = (
  url: string,
  options?: UseGetExpressRatesOptions,
): ExpressRateReturnType => {
  const hash = [keys.read, url];
  const isEnabled = options?.enabled ?? true;

  const {
    isLoading,
    data = null,
    isSuccess,
    error,
    refetch,
    dataUpdatedAt,
  }: UseQueryResult<EXPRESS_RATES_API_RESPONSE | null, unknown> = useQuery(
    hash,
    async () => {
      if (!isEnabled) return null;
      return await apiClient.get({ url });
    },
    {
      ...(options?.staleTime !== undefined && { staleTime: options.staleTime }),
      ...(options?.refetchInterval !== undefined && {
        refetchInterval: options.refetchInterval,
      }),
      enabled: isEnabled,
      // Keep previous data while fetching new rate for smooth UX
      keepPreviousData: true,
    },
  );

  return {
    isLoading: isEnabled ? isLoading : false,
    data,
    isSuccess,
    error,
    refetch,
    dataUpdatedAt,
  };
};

const useGetRates = (
  url: string,
  pageNumber: string,
  pageSize: string,
): RatesReturnType => {
  const hash = [keys.read, `${pageNumber}-${pageSize}`];
  const {
    isLoading,
    data = null,
    isSuccess,
    error,
  }: UseQueryResult<RATES_API_RESPONSE | null, unknown> = useQuery(
    hash,
    async () =>
      await apiClient.get({
        url,
      }),
  );
  return { isLoading, data, isSuccess, error };
};

const useGetRate = (rateId: string): RateReturnType => {
  const hash = [keys.read];
  const {
    isLoading,
    data = null,
    isSuccess,
    error,
  }: UseQueryResult<RATE_API_RESPONSE | null, unknown> = useQuery(
    hash,
    async () =>
      await apiClient.get({ url: `${baseURL}/${ratesResourcePath}/${rateId}` }),
  );
  return { isLoading, data, isSuccess, error };
};

const useGetWalletFundingRate = (currencyPair: string): RateReturnType => {
  const hash = [keys.read, `wallets/${currencyPair}`];
  const {
    isLoading,
    data = null,
    isSuccess,
    error,
  }: UseQueryResult<RATE_API_RESPONSE | null, unknown> = useQuery(
    hash,
    async () =>
      await apiClient.get({
        url: `${baseURL}/${ratesResourcePath}/wallets?currencyPair=${currencyPair}`,
      }),
  );
  return { isLoading, data, isSuccess, error };
};

export { useGetRates, useGetRate, useGetExpressRates, useGetWalletFundingRate };
